import { Component } from './component.js';
import { VariantSelectedEvent, VariantUpdateEvent } from './events.js';
import { morph, MORPH_OPTIONS } from './morph.js';
import { OverflowList } from './overflow-list.js';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from './utilities.js';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('./component.js').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_START
    // 作用：页面首次加载时，也立即禁止不可用选项点击。
    // 还原：删除这一行。
    this.lockHoooyiUnavailableOptions();
    // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_END
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    // HOOYI_FAST_VARIANT_DATA_START
    // 作用：
    // 1. 先用页面内 data-hoooyi-variant-map 做本地变体计算。
    // 2. 立即更新 Color 文案、Size 可用状态、input[name="id"]、Add to cart 按钮状态。
    // 3. 避免这些关键交互等待 /products?...section_id=... 请求返回。
    //
    // 注意：
    // 原因：当前主题里把完整 section 快照传给 VariantUpdateEvent 可能会导致状态被旧 DOM 覆盖。
    //
    // 还原方法：
    // 1. 删除 hoooyiLocalVariant 这一段。
    // 2. 删除后面 variantId 使用 hoooyiLocalVariant 的改动。
    // 3. 删除本文件后面新增的 HOOYI_FAST_VARIANT_DATA 方法块。
    const hoooyiLocalVariant = this.hoooyiFastUpdateVariantData(selectedOption);

    if (hoooyiLocalVariant) {
      const emptyHtml = document.implementation.createHTMLDocument('');

      this.dispatchEvent(
        new VariantUpdateEvent(hoooyiLocalVariant, this.selectedOptionId, {
          html: emptyHtml,
          productId: this.dataset.productId ?? '',
        })
      );
    }
    // HOOYI_FAST_VARIANT_DATA_END

    // HOOYI_FAST_VARIANT_MEDIA_START
    // 作用：只允许 Color 选项触发主图快速切换。
    // 原因：Size input 上也可能有 data-hoooyi-media-src，点击 Size 会误切到其他颜色图片。
    // 还原：恢复为 this.fastUpdateMedia(selectedOption);
    // if (
    //   typeof this.fastUpdateMedia === 'function' &&
    //   Number.parseInt(selectedOption.dataset.fieldsetIndex || '0', 10) === 0 &&
    //   !selectedOption.closest('quick-add-dialog')
    // ) {
    //   this.fastUpdateMedia(selectedOption);
    // }
    // HOOYI_FAST_VARIANT_MEDIA_END

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    // HOOYI_FAST_VARIANT_DATA_START
    // 作用：
    // 如果本地已经算出最终 variant，就优先使用本地 variant id 更新 URL。
    // 还原方法：
    // 把下面这一行替换回原主题写法：
    // const variantId = selectedOption.dataset.variantId || null;
    const variantId = hoooyiLocalVariant?.id ? String(hoooyiLocalVariant.id) : selectedOption.dataset.variantId || null;
    // HOOYI_FAST_VARIANT_DATA_END

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  // /**
  //  * Handles the variant change event.
  //  * @param {Event} event - The variant change event.
  //  */
  // variantChanged(event) {
  //   if (!(event.target instanceof HTMLElement)) return;

  //   const selectedOption =
  //     event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

  //   if (!selectedOption) return;

  //   this.updateSelectedOption(event.target);
  //   this.dispatchEvent(new VariantSelectedEvent({
  //     id: selectedOption.dataset.optionValueId ?? '',
  //   }));

  //   // HOOYI_FAST_VARIANT_MEDIA_START
  //   // 作用：在主题发起 /products?...option_values 请求之前，先用 Liquid 写入的图片 URL 立即切换可见主图。
  //   // 注意：这里只做“体感提速”，不替代原主题的 fetchUpdatedSection；价格、库存、按钮状态仍由原逻辑更新。
  //   // 还原：删除这一行调用，并删除下面新增的 fastUpdateMedia / cleanHoooyiSrcset / preloadHoooyiImage / getLargestVisibleProductImage 方法。
  //   this.fastUpdateMedia(selectedOption);
  //   // HOOYI_FAST_VARIANT_MEDIA_END

  //   const isOnProductPage =
  //     this.dataset.templateProductMatch === 'true' &&
  //     !event.target.closest('product-card') &&
  //     !event.target.closest('quick-add-dialog');

  //   // Morph the entire main content for combined listings child products, because changing the product
  //   // might also change other sections depending on recommendations, metafields, etc.
  //   const currentUrl = this.dataset.productUrl?.split('?')[0];
  //   const newUrl = selectedOption.dataset.connectedProductUrl;
  //   const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
  //   const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

  //   const morphElementSelector = loadsNewProduct
  //     ? 'main'
  //     : isOnFeaturedProductSection
  //     ? 'featured-product-information'
  //     : undefined;

  //   this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

  //   const url = new URL(window.location.href);

  //   const variantId = selectedOption.dataset.variantId || null;

  //   if (isOnProductPage) {
  //     if (variantId) {
  //       url.searchParams.set('variant', variantId);
  //     } else {
  //       url.searchParams.delete('variant');
  //     }
  //   }

  //   // Change the path if the option is connected to another product via combined listing.
  //   if (loadsNewProduct) {
  //     url.pathname = newUrl;
  //   }

  //   if (url.href !== window.location.href) {
  //     yieldToMainThread().then(() => {
  //       history.replaceState({}, '', url.toString());
  //     });
  //   }
  // }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (productUrl?.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    // HOOYI_FAST_VARIANT_DATA_START
    // 作用：
    // 普通商品详情页切换 Color / Size 时，不再请求完整商品页 HTML。
    // 改成请求当前 product section：
    // /products/xxx?section_id=当前section&option_values=...
    //
    // 原因：
    // 原主题普通商品页默认请求 /products/xxx?option_values=...，
    // 会返回完整页面 HTML，导致 Color 文案、Size 状态、Add to cart variant id 更新很慢。
    //
    // 还原：
    // 删除这段 HOOYI_FAST_VARIANT_DATA_START 到 HOOYI_FAST_VARIANT_DATA_END，
    // 恢复下面原主题的 SECTION_ID_MAP 逻辑和最后 return。
    const isQuickOrCard =
      this.closest('quick-add-component') ||
      this.closest('swatches-variant-picker-component') ||
      this.closest('featured-product-information') ||
      this.closest('product-card') ||
      this.closest('quick-add-dialog');

    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = !!connectedProductUrl && connectedProductUrl !== currentProductUrl;

    const mainProductSectionId = this.dataset.sectionId;

    if (mainProductSectionId && !isQuickOrCard && !loadsNewProduct) {
      const query = params.filter(Boolean).join('&');
      return `${productUrl}?section_id=${mainProductSectionId}${query ? `&${query}` : ''}`;
    }
    // HOOYI_FAST_VARIANT_DATA_END

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  // HOOYI_FAST_VARIANT_MEDIA_START
  /**
   * Fast switches visible product image before the section fetch completes.
   *
   * Why:
   * When the page has not fully loaded, the theme's media gallery / lazy image logic
   * may still overwrite the image after the first fast switch.
   *
   * What this version does:
   * 1. Immediately switches the largest visible product image.
   * 2. Re-applies the same image several times while the page is still settling.
   * 3. Uses a token to prevent old delayed retries from overriding a newer color click.
   *
   * Rollback:
   * Remove this method group and remove this.fastUpdateMedia(selectedOption) in variantChanged().
   *
   * @param {HTMLElement} selectedOption
   */
  fastUpdateMedia(selectedOption) {
    if (!(selectedOption instanceof HTMLElement)) return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !selectedOption.closest('product-card') &&
      !selectedOption.closest('quick-add-dialog');

    if (!isOnProductPage) return;

    const src = selectedOption.dataset.hoooyiMediaSrc;
    const srcset = this.cleanHoooyiSrcset(selectedOption.dataset.hoooyiMediaSrcset);

    if (!src) return;

    // Each click gets a new token.
    // Delayed retries from older clicks will be ignored.
    this.__hoooyiFastMediaToken = (this.__hoooyiFastMediaToken || 0) + 1;
    const token = this.__hoooyiFastMediaToken;

    this.preloadHoooyiImage(src, srcset);

    const apply = () => {
      if (token !== this.__hoooyiFastMediaToken) return;

      const mainImg = this.getLargestVisibleProductImage();
      if (!mainImg) return;

      this.applyHoooyiMediaToImage(mainImg, src, srcset);
    };

    // Apply immediately.
    apply();

    // Re-apply while the page is still loading / gallery is still initializing.
    // This fixes the issue where early page-load image logic overwrites our first switch.
    [16, 50, 120, 250, 500, 900, 1500, 2500, 4000].forEach((delay) => {
      window.setTimeout(apply, delay);
    });

    // Also apply on the next few animation frames.
    // This helps when DOM layout is not ready at the exact change event moment.
    let frames = 0;
    const rafApply = () => {
      if (token !== this.__hoooyiFastMediaToken) return;
      apply();

      frames += 1;
      if (frames < 10) {
        window.requestAnimationFrame(rafApply);
      }
    };

    window.requestAnimationFrame(rafApply);
  }

  /**
   * Applies target media src/srcset to a visible image.
   *
   * @param {HTMLImageElement} img
   * @param {string} src
   * @param {string} srcset
   */
  applyHoooyiMediaToImage(img, src, srcset) {
    if (!img || !src) return;

    const currentSrc = (img.currentSrc || img.src || '').split('?')[0];
    const targetSrc = String(src).split('?')[0];

    // If already on the target image, do not keep rewriting unnecessarily.
    if (currentSrc === targetSrc) return;

    const picture = img.closest('picture');

    if (picture && srcset) {
      picture.querySelectorAll('source').forEach((source) => {
        source.srcset = srcset;
      });
    }

    img.removeAttribute('loading');
    img.loading = 'eager';
    img.setAttribute('fetchpriority', 'high');

    if (srcset) {
      img.srcset = srcset;
    }

    img.src = src;
  }

  /**
   * Normalizes Liquid-generated srcset spacing.
   *
   * @param {string | undefined} srcset
   * @returns {string}
   */
  cleanHoooyiSrcset(srcset) {
    return String(srcset || '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Starts loading the target image as early as possible.
   *
   * @param {string} src
   * @param {string} srcset
   */
  preloadHoooyiImage(src, srcset) {
    if (!src) return;

    const img = new Image();

    if (srcset) {
      img.srcset = srcset;
    }

    img.src = src;
  }

  /**
   * Finds the largest visible image in the product page area.
   *
   * This is intentionally not limited to a single theme selector because the new Shopify
   * theme uses dynamic media-gallery markup and can change while the page is loading.
   *
   * @returns {HTMLImageElement | undefined}
   */
  getLargestVisibleProductImage() {
    const scope =
      this.closest('main') ||
      document.querySelector('main') ||
      document.body;

    const imgs = Array.from(scope.querySelectorAll('img'));

    return imgs
      .map((img) => {
        const rect = img.getBoundingClientRect();
        const style = window.getComputedStyle(img);

        return {
          img,
          area: rect.width * rect.height,
          visible:
            rect.width > 160 &&
            rect.height > 160 &&
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < window.innerHeight &&
            rect.left < window.innerWidth &&
            style.display !== 'none' &&
            style.visibility !== 'hidden' &&
            Number(style.opacity) !== 0,
        };
      })
      .filter((item) => item.visible)
      .sort((a, b) => b.area - a.area)[0]?.img;
  }
  // HOOYI_FAST_VARIANT_MEDIA_END

  // HOOYI_FAST_QUICK_ADD_START
  /**
   * Checks whether current variant picker is inside quick add dialog.
   *
   * 作用：
   * quick add 弹窗里不执行详情页专用逻辑，比如底部 sticky 浮窗扫描。
   *
   * 还原：
   * 删除本方法，并删除调用 isHoooyiQuickAddContext() 的判断。
   *
   * @returns {boolean}
   */
  isHoooyiQuickAddContext() {
    return Boolean(
      this.closest('quick-add-dialog') ||
        this.closest('dialog') ||
        this.closest('[data-quick-add-modal]') ||
        this.closest('[data-product-modal]')
    );
  }
  /**
   * Gets the correct update root.
   *
   * 作用：
   * 详情页时，更新当前 product section。
   * Quick add 弹窗时，只更新当前弹窗，避免误改详情页主图/主表单。
   *
   * 还原：
   * 删除本方法，并把使用 getHoooyiUpdateRoot() 的地方恢复成 this.closest('.shopify-section') || document。
   *
   * @returns {Element | Document}
   */
  getHoooyiUpdateRoot() {
    return (
      this.closest('quick-add-dialog') ||
      this.closest('dialog') ||
      this.closest('[data-quick-add-modal]') ||
      this.closest('.shopify-section') ||
      document
    );
  }
  // HOOYI_FAST_QUICK_ADD_END

  // HOOYI_FAST_VARIANT_DATA_START
  /**
   * Reads local variant map from Liquid output.
   *
   * 来源：
   * snippets/variant-main-picker.liquid 里的 <script data-hoooyi-variant-map>
   *
   * 还原方法：
   * 删除 HOOYI_FAST_VARIANT_DATA_START 到 HOOYI_FAST_VARIANT_DATA_END 之间所有方法。
   *
   * @returns {{productId: string, options: string[], variants: Array<any>} | null}
   */
  getHoooyiVariantMap() {
    if (this.__hoooyiVariantMap !== undefined) return this.__hoooyiVariantMap;

    const script = this.querySelector('script[data-hoooyi-variant-map]');

    if (!script?.textContent) {
      this.__hoooyiVariantMap = null;
      return null;
    }

    try {
      this.__hoooyiVariantMap = JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[HOOYI_FAST_VARIANT_DATA] variant map parse failed', error);
      this.__hoooyiVariantMap = null;
    }

    return this.__hoooyiVariantMap;
  }

  /**
   * Gets current selected option values in fieldset order.
   * Example: ["Black", "M"]
   *
   * @returns {string[]}
   */
  getHoooyiSelectedOptionValues() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    return fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });
  }

  /**
   * Finds the best variant based on current selection.
   *
   * 例子：
   * 原来是 Tan + S。
   * 点击 Black。
   * 如果 Black + S 不存在或不可售，就自动找 Black 下第一个可售尺码，比如 Black + M。
   *
   * @param {any} map
   * @param {string[]} selectedValues
   * @param {number} changedIndex
   * @returns {any | null}
   */
  findHoooyiPreferredVariant(map, selectedValues, changedIndex) {
    if (!map?.variants?.length) return null;

    const exact = map.variants.find((variant) => {
      return variant.options.every((value, index) => value === selectedValues[index]);
    });

    if (exact?.available) return exact;

    return (
      map.variants.find((variant) => {
        if (!variant.available) return false;

        for (let index = 0; index <= changedIndex; index += 1) {
          if (selectedValues[index] && variant.options[index] !== selectedValues[index]) {
            return false;
          }
        }

        return true;
      }) ||
      exact ||
      null
    );
  }

  /**
   * Syncs radio checked state to the selected variant's real options.
   *
   * 例子：
   * 用户点 Black，但当前 Size=S 不可用。
   * 本方法会把 Size 自动改成 Black 下第一个可售尺码，比如 M。
   *
   * @param {string[]} optionValues
   */
  syncHoooyiInputsToOptionValues(optionValues) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    optionValues.forEach((value, fieldsetIndex) => {
      const fieldset = fieldsets[fieldsetIndex];

      if (!fieldset) return;

      const input = Array.from(fieldset.querySelectorAll('input')).find((item) => {
        return item instanceof HTMLInputElement && item.value === value;
      });

      if (input instanceof HTMLInputElement && !input.checked) {
        this.updateSelectedOption(input);
      }
    });
  }

  /**
   * Updates visible option label text.
   *
   * 例子：
   * Color Tan -> Color Black
   */
  updateHoooyiLegendTexts() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const valueEl = fieldset.querySelector('legend .variant-option__swatch-value');

      if (checked instanceof HTMLInputElement && valueEl) {
        valueEl.textContent = checked.value;
      }
    });
  }

  // HOOYI_FAST_VARIANT_DATA_START
  /**
   * 本地同步 Color / Size 的可用状态。
   *
   * 解决的问题：
   * 原主题的不可选状态是服务端 Liquid 渲染出来的，
   * 如果不等 /products?...section_id=... 返回，页面不会自动出现/消失斜杠。
   *
   * 这里做的事：
   * 1. 根据本地 variant map 判断每个 option 是否可用；
   * 2. 立即更新 input 的 aria-disabled / data-option-available；
   * 3. 立即给 label 加灰态；
   * 4. 立即新增/删除 .variant-option__strikethrough 斜杠 SVG。
   *
   * 还原方法：
   * 删除 HOOYI_FAST_VARIANT_DATA_START 到 HOOYI_FAST_VARIANT_DATA_END，
   * 并恢复之前的 updateHoooyiOptionAvailability(map)。
   *
   * @param {any} map
   */
  updateHoooyiOptionAvailability(map) {
    if (!map?.variants?.length) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const selectedValues = this.getHoooyiSelectedOptionValues();

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = Array.from(fieldset.querySelectorAll('input'));

      inputs.forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        const isAvailable = map.variants.some((variant) => {
          if (!variant.available) return false;

          for (let index = 0; index < fieldsetIndex; index += 1) {
            if (selectedValues[index] && variant.options[index] !== selectedValues[index]) {
              return false;
            }
          }

          return variant.options[fieldsetIndex] === input.value;
        });

        this.applyHoooyiAvailabilityToInput(input, isAvailable);
      });
    });
  }

  /**
   * 把可用/不可用状态真正应用到页面上。
   *
   * 作用：
   * 1. 更新 input 的 data-option-available；
   * 2. 更新 aria-disabled；
   * 3. 不可用时真正 disabled，防止用户点击 S / L 这种当前颜色下不可选的尺码；
   * 4. 给 label 加 hoooyi-option-unavailable；
   * 5. 立即新增/删除斜杠 SVG。
   *
   * 还原方法：
   * 删除本方法，恢复原来的 applyHoooyiAvailabilityToInput。
   *
   * @param {HTMLInputElement} input
   * @param {boolean} isAvailable
   */
  applyHoooyiAvailabilityToInput(input, isAvailable) {
    const label = input.closest('label');

    input.dataset.optionAvailable = String(isAvailable);

    if (isAvailable) {
      input.disabled = false;
      input.removeAttribute('aria-disabled');
      input.removeAttribute('data-hoooyi-option-unavailable');
    } else {
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
      input.setAttribute('data-hoooyi-option-unavailable', 'true');
    }

    if (!label) return;

    label.classList.toggle('hoooyi-option-unavailable', !isAvailable);
    label.toggleAttribute('data-hoooyi-option-unavailable', !isAvailable);

    const existingStrike = label.querySelector('.variant-option__strikethrough');

    if (!isAvailable && !existingStrike) {
      label.appendChild(this.createHoooyiStrikethroughSvg());
    }

    if (isAvailable && existingStrike) {
      existingStrike.remove();
    }
  }


  /**
   * 创建和 snippets/strikethrough-variant.liquid 类似的斜杠 SVG。
   *
   * 原 Liquid 里就是根据 product_option.available 渲染 .variant-option__strikethrough。
   * 但它只有等远程 section 返回才会变化，所以这里本地补一个。
   *
   * @returns {SVGSVGElement}
   */
  createHoooyiStrikethroughSvg() {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 100 46');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('class', 'variant-option__strikethrough');
    svg.setAttribute('aria-hidden', 'true');

    const line1 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line1.setAttribute('x1', '100');
    line1.setAttribute('y1', '0');
    line1.setAttribute('x2', '0');
    line1.setAttribute('y2', '46');
    line1.setAttribute('vector-effect', 'non-scaling-stroke');

    const line2 = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line2.setAttribute('x1', '100');
    line2.setAttribute('y1', '0');
    line2.setAttribute('x2', '0');
    line2.setAttribute('y2', '46');
    line2.setAttribute('vector-effect', 'non-scaling-stroke');

    svg.appendChild(line1);
    svg.appendChild(line2);

    return svg;
  }

 // HOOYI_FAST_VARIANT_MEDIA_GALLERY_START
  /**
   * 作用：
   * 1. 详情页：继续同步主图、左侧小图、媒体顺序；
   * 2. Quick add 弹窗：只在弹窗作用域内同步主图；
   * 3. 避免弹窗切色卡时误改详情页主图。
   *
   * 还原：
   * 1. 删除本方法；
   * 2. 删除 hoooyiFastUpdateVariantData() 里的 this.updateHoooyiMediaGalleryFast(preferredVariant) 调用。
   *
   * @param {any} variant
   */
  updateHoooyiMediaGalleryFast(variant) {
    const mediaId = String(variant?.featured_media_id || '');
    const root = this.getHoooyiUpdateRoot?.() || document;

    // HOOYI_FAST_QUICK_ADD_MEDIA_START
    // 作用：Quick add 弹窗里不走详情页 media-gallery / slideshow-controls 重排逻辑。
    // 原因：弹窗一般只有一个主图区域，强行走详情页小图重排会命不中或被主题组件延迟覆盖。
    // 还原：删除 HOOYI_FAST_QUICK_ADD_MEDIA_START 到 HOOYI_FAST_QUICK_ADD_MEDIA_END。
    const isQuickAddRoot =
      this.closest('quick-add-dialog') ||
      this.closest('dialog') ||
      this.closest('[data-quick-add-modal]') ||
      this.closest('[data-product-modal]');

    if (isQuickAddRoot) {
      this.updateHoooyiLargestImageInRootFast(root, variant);
      return;
    }
    // HOOYI_FAST_QUICK_ADD_MEDIA_END

 
    // HOOYI_PDP_MEDIA_SAFE_GUARD_START
    // 作用：
    // 详情页只允许通过 media-gallery / slideshow-controls 精确更新商品主图；
    // 如果没有 mediaId，就不要兜底改任意图片，避免右侧 banner 被替换成商品图。
    // 还原：删除本段，恢复原来的 if (!mediaId) fallback。
    if (!mediaId) return;
    // HOOYI_PDP_MEDIA_SAFE_GUARD_END

    const map = this.getHoooyiVariantMap?.();
    const mediaOrder = Array.isArray(map?.media_order)
      ? map.media_order.map((id) => String(id)).filter(Boolean)
      : [];

    const gallery =
      root.querySelector?.('media-gallery') ||
      root.querySelector?.('[id*="MediaGallery"]');

    // HOOYI_PDP_MEDIA_SAFE_GUARD_START
    // 作用：详情页找不到 media-gallery 时，不做“最大图片”兜底，避免误改 banner。
    // 还原：恢复为 this.updateHoooyiLargestImageInRootFast(root, variant); return;
    if (!gallery) return;
    // HOOYI_PDP_MEDIA_SAFE_GUARD_END

    const slideshow = gallery.querySelector('slideshow-component');
    const slidesWrapper = gallery.querySelector('slideshow-slides');
    const thumbnailList = gallery.querySelector('.slideshow-controls__thumbnails');

    // HOOYI_PDP_MEDIA_SAFE_GUARD_START
    // 作用：详情页 gallery 结构不完整时，不做最大图片兜底。
    // updateHoooyiLargestImageInRootFast 只能给 quick add 弹窗用，不能给详情页用，避免 banner 被替换。
    if (!slideshow || !slidesWrapper || !thumbnailList || !mediaOrder.length) {
      return;
    }
    // HOOYI_PDP_MEDIA_SAFE_GUARD_END

    const slides = Array.from(slidesWrapper.children).filter((el) => {
      return el.tagName && el.tagName.toLowerCase() === 'slideshow-slide';
    });

    const thumbnails = Array.from(
      thumbnailList.querySelectorAll('.slideshow-controls__thumbnail')
    );

    function getSlideMediaId(slide) {
      const media = slide.querySelector('.product-media[data-media-id], [data-media-id]');
      return media?.dataset?.mediaId ? String(media.dataset.mediaId) : '';
    }

    const slideByMediaId = new Map();
    slides.forEach((slide) => {
      const id = getSlideMediaId(slide);
      if (id && !slideByMediaId.has(id)) {
        slideByMediaId.set(id, slide);
      }
    });

    const thumbnailByMediaId = new Map();
    thumbnails.forEach((thumbnail) => {
      const id = thumbnail.dataset?.mediaId ? String(thumbnail.dataset.mediaId) : '';
      if (id && !thumbnailByMediaId.has(id)) {
        thumbnailByMediaId.set(id, thumbnail);
      }
    });

    const targetSlide = slideByMediaId.get(mediaId);
    const targetThumbnail = thumbnailByMediaId.get(mediaId);

    // HOOYI_PDP_MEDIA_SAFE_GUARD_START
    // 作用：详情页找不到对应 media 时，不要兜底改最大图片，避免 banner 被误改。
    // 还原：恢复为 this.updateHoooyiLargestImageInRootFast(root, variant); return;
    if (!targetSlide || !targetThumbnail) {
      return;
    }
    // HOOYI_PDP_MEDIA_SAFE_GUARD_END

    const orderedMediaIds = [
      mediaId,
      ...mediaOrder.filter((id) => id !== mediaId),
    ];

    gallery.classList.add('hoooyi-media-gallery-reordering');

    thumbnails.forEach((thumbnail) => {
      const isTarget = thumbnail === targetThumbnail;

      thumbnail.setAttribute('aria-selected', String(isTarget));

      if (isTarget) {
        thumbnail.setAttribute('aria-current', 'true');
        thumbnail.classList.add('is-active');
      } else {
        thumbnail.removeAttribute('aria-current');
        thumbnail.classList.remove('is-active', 'active', 'selected');
      }
    });

    slides.forEach((slide) => {
      slide.setAttribute('aria-hidden', String(slide !== targetSlide));
    });

    const slideFragment = document.createDocumentFragment();

    orderedMediaIds.forEach((id) => {
      const slide = slideByMediaId.get(id);
      if (slide) {
        slideFragment.appendChild(slide);
      }
    });

    slides.forEach((slide) => {
      if (slide.parentNode === slidesWrapper) {
        slideFragment.appendChild(slide);
      }
    });

    slidesWrapper.appendChild(slideFragment);

    const thumbnailFragment = document.createDocumentFragment();

    orderedMediaIds.forEach((id) => {
      const thumbnail = thumbnailByMediaId.get(id);
      if (thumbnail) {
        thumbnailFragment.appendChild(thumbnail);
      }
    });

    thumbnails.forEach((thumbnail) => {
      if (thumbnail.parentNode === thumbnailList) {
        thumbnailFragment.appendChild(thumbnail);
      }
    });

    thumbnailList.appendChild(thumbnailFragment);

    try {
      slideshow.updatedCallback?.();
    } catch (error) {
      console.warn('[HOOYI_FAST_VARIANT_MEDIA_GALLERY] updatedCallback failed', error);
    }

    const slidesAfterMove = Array.from(slidesWrapper.children).filter((el) => {
      return el.tagName && el.tagName.toLowerCase() === 'slideshow-slide';
    });

    const thumbnailsAfterMove = Array.from(
      thumbnailList.querySelectorAll('.slideshow-controls__thumbnail')
    );

    slidesAfterMove.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== 0));

      const oldStyle = slide.getAttribute('style') || '';
      const newStyle = oldStyle.replace(/--slide-\d+/g, `--slide-${index}`);
      slide.setAttribute('style', newStyle);
    });

    thumbnailsAfterMove.forEach((thumbnail, index) => {
      const isSelected = index === 0;

      thumbnail.setAttribute('aria-selected', String(isSelected));

      if (isSelected) {
        thumbnail.setAttribute('aria-current', 'true');
        thumbnail.classList.add('is-active');
      } else {
        thumbnail.removeAttribute('aria-current');
        thumbnail.classList.remove('is-active', 'active', 'selected');
      }

      if (thumbnail.hasAttribute('on:click')) {
        thumbnail.setAttribute('on:click', `/select/${index}`);
      }

      const ariaLabel = thumbnail.getAttribute('aria-label');

      if (ariaLabel) {
        thumbnail.setAttribute(
          'aria-label',
          ariaLabel.replace(/Slide\s+\d+\s+of/i, `Slide ${index + 1} of`)
        );
      }
    });

    slideshow.setAttribute('initial-slide', '0');

    try {
      slideshow.current = 0;
    } catch (error) {
      // 不影响主流程
    }

    if (typeof slidesWrapper.scrollTo === 'function') {
      slidesWrapper.scrollTo({
        left: 0,
        top: 0,
        behavior: 'instant',
      });
    }

    requestAnimationFrame(() => {
      const latestThumbnails = Array.from(
        thumbnailList.querySelectorAll('.slideshow-controls__thumbnail')
      );

      latestThumbnails.forEach((thumbnail, index) => {
        thumbnail.setAttribute('aria-selected', String(index === 0));

        if (index === 0) {
          thumbnail.setAttribute('aria-current', 'true');
          thumbnail.classList.add('is-active');
        } else {
          thumbnail.removeAttribute('aria-current');
          thumbnail.classList.remove('is-active', 'active', 'selected');
        }
      });

      gallery.classList.remove('hoooyi-media-gallery-reordering');
    });
  }

  // HOOYI_FAST_QUICK_ADD_MEDIA_START
  /**
   * Updates the largest visible product image inside a scoped root.
   *
   * 作用：
   * Quick add 弹窗切换色卡时，立即更新弹窗主图。
   *
   * 当前版本额外解决：
   * 快速连续切色卡时，旧颜色的 setTimeout 补写不能再覆盖新颜色图片。
   *
   * 例子：
   * Green -> Dark 快速切换：
   * Green 的延迟补写会因为 token 过期而失效，不会再把 Dark 图片闪回 Green。
   *
   * 还原：
   * 删除本方法，恢复之前的 updateHoooyiLargestImageInRootFast。
   *
   * @param {Element | Document} root
   * @param {any} variant
   */
  updateHoooyiLargestImageInRootFast(root, variant) {
    const src = variant?.featured_media_src || variant?.featured_media_preview_src;

    if (!src || !root?.querySelectorAll) return;

    // HOOYI_QUICK_ADD_IMAGE_TOKEN_START
    // 作用：每次切换图片生成新的 token，旧颜色的延迟补写全部失效。
    // 还原：删除 token 相关判断。
    this.__hoooyiQuickAddImageToken = (this.__hoooyiQuickAddImageToken || 0) + 1;
    const token = this.__hoooyiQuickAddImageToken;
    // HOOYI_QUICK_ADD_IMAGE_TOKEN_END

    const imgs = Array.from(root.querySelectorAll('img')).filter((img) => {
      if (!(img instanceof HTMLImageElement)) return false;
      if (img.closest('variant-picker')) return false;

      const rect = img.getBoundingClientRect();
      const style = window.getComputedStyle(img);

      return (
        rect.width > 120 &&
        rect.height > 120 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity) !== 0
      );
    });

    const targetImg = imgs
      .map((img) => ({
        img,
        area: img.getBoundingClientRect().width * img.getBoundingClientRect().height,
      }))
      .sort((a, b) => b.area - a.area)[0]?.img;

    if (!targetImg) return;

    const preload = new Image();
    preload.src = src;

    const applyImage = () => {
      // HOOYI_QUICK_ADD_IMAGE_TOKEN_START
      // 作用：如果这已经不是最后一次切换，就不允许旧图片写回。
      // 还原：删除这个 if。
      if (token !== this.__hoooyiQuickAddImageToken) return;
      // HOOYI_QUICK_ADD_IMAGE_TOKEN_END

      if (!document.body.contains(targetImg)) return;

      const picture = targetImg.closest('picture');

      if (picture) {
        picture.querySelectorAll('source').forEach((source) => {
          source.srcset = src;
          source.setAttribute('srcset', src);
          source.dataset.srcset = src;
        });
      }

      targetImg.removeAttribute('loading');
      targetImg.loading = 'eager';
      targetImg.setAttribute('fetchpriority', 'high');

      targetImg.src = src;
      targetImg.srcset = src;
      targetImg.dataset.src = src;
      targetImg.dataset.srcset = src;
    };

    applyImage();

    /**
     * 一些主题图片组件会在下一帧重新写回旧 src，
     * 所以短时间内补几次。
     * 但每次补写前都会检查 token，避免旧颜色覆盖新颜色。
     */
    [50, 150, 350, 700].forEach((delay) => {
      window.setTimeout(applyImage, delay);
    });
  }
  // HOOYI_FAST_QUICK_ADD_MEDIA_END
  // HOOYI_FAST_VARIANT_MEDIA_GALLERY_END

  // HOOYI_FAST_VARIANT_DATA_END

  // /**
  //  * Updates local option availability.
  //  *
  //  * 逻辑：
  //  * - Color 是否可用：看该 Color 下有没有任一 available variant。
  //  * - Size 是否可用：看当前 Color + 当前 Size 是否有 available variant。
  //  *
  //  * @param {any} map
  //  */
  // updateHoooyiOptionAvailability(map) {
  //   if (!map?.variants?.length) return;

  //   const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
  //   const selectedValues = this.getHoooyiSelectedOptionValues();

  //   fieldsets.forEach((fieldset, fieldsetIndex) => {
  //     const inputs = Array.from(fieldset.querySelectorAll('input'));

  //     inputs.forEach((input) => {
  //       if (!(input instanceof HTMLInputElement)) return;

  //       const isAvailable = map.variants.some((variant) => {
  //         if (!variant.available) return false;

  //         for (let index = 0; index < fieldsetIndex; index += 1) {
  //           if (selectedValues[index] && variant.options[index] !== selectedValues[index]) {
  //             return false;
  //           }
  //         }

  //         return variant.options[fieldsetIndex] === input.value;
  //       });

  //       input.dataset.optionAvailable = String(isAvailable);

  //       if (isAvailable) {
  //         input.removeAttribute('aria-disabled');
  //       } else {
  //         input.setAttribute('aria-disabled', 'true');
  //       }
  //     });
  //   });
  // }

  // HOOYI_FAST_VARIANT_BUTTON_TEXT_START
  /**
   * Updates Add to cart button text immediately.
   *
   * 作用：
   * 原主题的按钮文案是等远程 section 返回后，通过 product-form.js 里的 morph(currentAddToCartButton, newAddToCartButton) 更新。
   * 现在我们本地已经知道 variant.available，所以直接同步按钮文字。
   *
   * 还原方法：
   * 1. 删除本方法；
   * 2. 删除 updateHoooyiProductFormFast() 里对 this.updateHoooyiAddToCartButtonText(...) 的调用；
   * 3. 删除 variant-main-picker.liquid 里的 button_texts。
   *
   * @param {HTMLButtonElement} button
   * @param {string} text
   * @param {boolean} available
   */
  updateHoooyiAddToCartButtonText(button, text, available) {
    if (!(button instanceof HTMLButtonElement)) return;

    const textTarget =
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content span') ||
      button.querySelector('.add-to-cart-text__content');

    if (textTarget) {
      textTarget.textContent = text;
    }

    const icon = button.querySelector('.add-to-cart-icon');

    if (icon instanceof HTMLElement) {
      icon.style.display = available ? '' : 'none';
    }

    button.setAttribute('aria-label', text);
    button.dataset.hoooyiButtonText = text;
  }
  // HOOYI_FAST_VARIANT_BUTTON_TEXT_END

  // HOOYI_FAST_VARIANT_DATA_START
  // HOOYI_FAST_STICKY_CART_PRICE_FIX_START
  /**
   * Converts possible HTML money string into plain text.
   *
   * 例子：
   * '<span class="money conversion-bear-money">$37.00</span>'
   * 会变成：
   * '$37.00'
   *
   * 还原：
   * 删除本方法，并恢复 updateHoooyiStickyCartFast() 里的 priceText 写法。
   *
   * @param {string | null | undefined} value
   * @returns {string}
   */
  cleanHoooyiMoneyText(value) {
    const raw = String(value || '').trim();

    if (!raw) return '';

    if (!raw.includes('<')) return raw;

    const div = document.createElement('div');
    div.innerHTML = raw;

    return div.textContent.trim();
  }
  // HOOYI_FAST_STICKY_CART_PRICE_FIX_END
  // HOOYI_FAST_STICKY_CART_START
  /**
   * Records latest variant for sticky cart and updates sticky only when user scrolls.
   *
   * 作用：
   * 1. 色卡切换时不立刻扫描 body 全部 DOM；
   * 2. 只记录当前最新 variant；
   * 3. 用户滚动、sticky 浮窗可能出现时，再同步 sticky 内容；
   * 4. 避免详情页主图/Color/Size 被 updateHoooyiStickyCartFast 阻塞 5 秒。
   *
   * 还原：
   * 删除本方法，并把 hoooyiFastUpdateVariantData() 里的调用恢复成
   * this.updateHoooyiStickyCartFast(preferredVariant);
   *
   * @param {any} variant
   */
  queueHoooyiStickyCartUpdate(variant) {
    if (!variant?.id) return;
    if (this.isHoooyiQuickAddContext?.()) return;

    this.__hoooyiLatestStickyVariant = variant;
    this.__hoooyiStickyNeedsUpdate = true;

    if (this.__hoooyiStickyScrollListenerBound) return;

    this.__hoooyiStickyScrollListenerBound = true;

    const onScroll = () => {
      if (!this.__hoooyiStickyNeedsUpdate) return;
      if (this.__hoooyiStickyScrollRaf) return;

      this.__hoooyiStickyScrollRaf = requestAnimationFrame(() => {
        this.__hoooyiStickyScrollRaf = 0;

        const latestVariant = this.__hoooyiLatestStickyVariant;

        if (!latestVariant?.id) return;

        this.__hoooyiStickyNeedsUpdate = false;
        this.updateHoooyiStickyCartFast(latestVariant);
      });
    };

    this.__hoooyiStickyScrollHandler = onScroll;
    window.addEventListener('scroll', onScroll, { passive: true });
  }
  /**
   * Schedules sticky cart update after critical variant UI has painted.
   *
   * 作用：
   * 1. 主图、Color、Size、Add to cart 先立即更新；
   * 2. 底部 sticky 浮窗延迟到下一帧再同步；
   * 3. 避免 updateHoooyiStickyCartFast() 扫描 body 全部节点，阻塞详情页色卡切换；
   * 4. quick add 弹窗里不执行 sticky 同步。
   *
   * 还原：
   * 删除本方法，并把 hoooyiFastUpdateVariantData() 里的调用恢复成
   * this.updateHoooyiStickyCartFast(preferredVariant);
   *
   * @param {any} variant
   */
  scheduleHoooyiStickyCartUpdate(variant) {
    if (!variant?.id) return;

    if (this.isHoooyiQuickAddContext?.()) return;

    this.__hoooyiStickyCartUpdateToken = (this.__hoooyiStickyCartUpdateToken || 0) + 1;
    const token = this.__hoooyiStickyCartUpdateToken;

    const run = () => {
      if (token !== this.__hoooyiStickyCartUpdateToken) return;
      this.updateHoooyiStickyCartFast(variant);
    };

    /**
     * 先让浏览器有机会把主图、Color、Size、按钮状态画出来。
     */
    requestAnimationFrame(() => {
      window.setTimeout(run, 0);
    });

    /**
     * sticky bar 可能是滚动后才出现，所以后面补几次。
     * 这里也带 token，快速切换时旧 variant 不会覆盖新 variant。
     */
    [250, 800, 1600].forEach((delay) => {
      window.setTimeout(run, delay);
    });
  }
  /**
   * Updates bottom sticky add-to-cart bar immediately.
   *
   * 作用：
   * 色卡/尺码切换后，立即同步底部浮窗：
   * 1. 图片；
   * 2. variant 文案，例如 Tan / XL -> Darkgreen / XL；
   * 3. 价格；
   * 4. input[name="id"]；
   * 5. Add to cart 按钮可用状态和文案。
   *
   * 为什么需要：
   * 底部浮窗是另一套 sticky DOM，不一定会被 updateHoooyiProductFormFast() 命中。
   * 原主题通常等 /products?...section_id=... 返回后才 morph 它，所以会有几秒延迟。
   *
   * 还原方法：
   * 1. 删除本方法；
   * 2. 删除 hoooyiFastUpdateVariantData() 里 this.updateHoooyiStickyCartFast(preferredVariant) 这一行。
   *
   * @param {any} variant
   */
  updateHoooyiStickyCartFast(variant) {
    if (!variant?.id) return;

    const map = this.getHoooyiVariantMap?.();
    const optionText = Array.isArray(variant.options) ? variant.options.filter(Boolean).join(' / ') : '';
    // HOOYI_FAST_STICKY_CART_PRICE_FIX_START
    // 作用：防止 variant.price 里混入 <span class="money ..."> 这类 HTML 字符串。
    // 还原：恢复为 const priceText = variant.price || '';
    const priceText = this.cleanHoooyiMoneyText?.(variant.price) || variant.price || '';
    // HOOYI_FAST_STICKY_CART_PRICE_FIX_END 

    const buttonText =
      variant.available === false
        ? map?.button_texts?.sold_out || 'Sold out'
        : map?.button_texts?.add_to_cart || 'Add to cart';

    /**
     * 找底部 sticky 浮窗。
     * 这里通过：
     * 1. fixed/sticky 定位；
     * 2. 在页面底部附近；
     * 3. 内部有加购按钮或 cart/add 表单；
     * 来定位。
     */
    const candidates = Array.from(document.querySelectorAll('body *')).filter((el) => {
      if (!(el instanceof HTMLElement)) return false;

      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();

      const isFloating = style.position === 'fixed' || style.position === 'sticky';
      const nearBottom = rect.bottom > window.innerHeight - 220;
      const largeEnough = rect.width > 260 && rect.height > 40;

      const hasCartControl = Boolean(
        el.querySelector('add-to-cart-component, form[action*="/cart/add"], button[name="add"], [ref="addToCartButton"]')
      );

      // HOOYI_STICKY_CART_CONTAINER_SAFE_START
      // 作用：避免把右侧 sticky 商品信息栏当成底部浮动加购条。
      // 右侧商品信息栏里有 Add to cart，也可能 position: sticky，并且包含 banner。
      // 如果被误选中，后续更新图片/文案会污染 banner。
      // 还原：恢复原来的 notMainProductArea 判断。
      const isProductInfoColumn = Boolean(
        el.closest('product-information') ||
          el.closest('.product-information') ||
          el.closest('[data-product-information]') ||
          el.closest('.product__info-container')
      );

      const containsBanner = Boolean(
        el.querySelector('.product-page-banner, [class*="banner"], [class*="Banner"]')
      );

      const looksLikeBottomBar =
        isFloating &&
        nearBottom &&
        largeEnough &&
        rect.height <= 180 &&
        rect.top >= window.innerHeight - 240;

      const notMainProductArea =
        !el.closest('media-gallery') &&
        !el.closest('cart-drawer') &&
        !el.closest('dialog') &&
        !isProductInfoColumn &&
        !containsBanner;

      return looksLikeBottomBar && hasCartControl && notMainProductArea;
      // HOOYI_STICKY_CART_CONTAINER_SAFE_END
    });

    /**
     * 去重：
     * 如果一个候选容器在另一个候选容器里面，只保留外层。
     */
    const stickyBars = candidates.filter((el) => {
      return !candidates.some((other) => other !== el && other.contains(el));
    });

    stickyBars.forEach((bar) => {
      /**
       * 1. 更新 sticky bar 里的 hidden variant id。
       */
      bar.querySelectorAll('input[name="id"], form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = String(variant.id);
        }
      });
      /**
       * 2. 更新 sticky bar 里的商品小缩略图。
       *
       * HOOYI_STICKY_CART_IMAGE_SAFE_START
       * 作用：
       * 只允许更新底部浮窗里的小商品图，不允许更新右侧 banner / reviews / app block 图片。
       *
       * 原因：
       * updateHoooyiStickyCartFast() 的 sticky bar 容器识别可能会误选中右侧 sticky 商品信息栏。
       * 如果直接 bar.querySelector('img')，就可能拿到右侧 banner，并把 banner 改成当前 variant 主图。
       *
       * 判断标准：
       * - 图片必须比较小，像 sticky 缩略图；
       * - 不能在 product page banner / media-gallery / accordion / app block 里面；
       * - 找不到符合条件的小图，就不更新图片。
       *
       * 还原：
       * 把 HOOYI_STICKY_CART_IMAGE_SAFE_START 到 HOOYI_STICKY_CART_IMAGE_SAFE_END
       * 替换回原来的 const img = bar.querySelector('img') 逻辑。
       */
      const stickyImg = Array.from(bar.querySelectorAll('img')).find((item) => {
        if (!(item instanceof HTMLImageElement)) return false;

        if (
          item.closest('media-gallery') ||
          item.closest('.product-media') ||
          item.closest('.product-media-container') ||
          item.closest('.product-page-banner') ||
          item.closest('[class*="banner"]') ||
          item.closest('[class*="Banner"]') ||
          item.closest('[class*="review"]') ||
          item.closest('[class*="Review"]') ||
          item.closest('.shopify-app-block')
        ) {
          return false;
        }

        const rect = item.getBoundingClientRect();

        return (
          rect.width > 24 &&
          rect.height > 24 &&
          rect.width <= 120 &&
          rect.height <= 120 &&
          rect.bottom > window.innerHeight - 180
        );
      });

      if (stickyImg instanceof HTMLImageElement && variant.featured_media_preview_src) {
        stickyImg.src = variant.featured_media_preview_src;
        stickyImg.removeAttribute('srcset');
      }
      /* HOOYI_STICKY_CART_IMAGE_SAFE_END */

      /**
       * 3. 更新变体组合文案，例如 Tan / XL。
       * 这个浮窗里通常只有一行包含 "/" 的短文本。
       */
      this.updateHoooyiStickyTextNode(bar, (text) => {
        const normalized = text.trim();

        if (!normalized.includes('/')) return null;
        if (normalized.length > 80) return null;

        return optionText;
      });

      /**
       * 4. 更新价格。
       */
      if (priceText) {
        this.updateHoooyiStickyTextNode(bar, (text) => {
          const normalized = text.trim();

          if (!normalized) return null;

          const looksLikePrice = /[$€£¥]\s?\d|USD|CAD|AUD|EUR|GBP/i.test(normalized);

          return looksLikePrice ? priceText : null;
        });
      }

      /**
       * 5. 更新 sticky add-to-cart 按钮状态和文案。
       */
      const buttons = bar.querySelectorAll('button[name="add"], [ref="addToCartButton"], add-to-cart-component button');

      buttons.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        button.disabled = !variant.available;
        button.setAttribute('aria-disabled', String(!variant.available));

        if (typeof this.updateHoooyiAddToCartButtonText === 'function') {
          this.updateHoooyiAddToCartButtonText(button, buttonText, variant.available);
        } else {
          button.textContent = buttonText;
        }
      });

      /**
       * 6. 如果 sticky bar 里有 add-to-cart-component，也同步 enable/disable。
       */
      bar.querySelectorAll('add-to-cart-component').forEach((container) => {
        if (variant.available) {
          container.enable?.();
        } else {
          container.disable?.();
        }

        if (variant.featured_media_preview_src) {
          container.setAttribute('data-product-variant-media', `${variant.featured_media_preview_src}&width=100`);
        }
      });
    });
  }

  /**
   * Updates matching text nodes inside sticky bar.
   *
   * @param {HTMLElement} root
   * @param {(text: string) => string | null} replacer
   */
  updateHoooyiStickyTextNode(root, replacer) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;

        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest('script, style, svg')) return NodeFilter.FILTER_REJECT;
        if (parent.closest('button')) return NodeFilter.FILTER_REJECT;

        const text = node.nodeValue || '';

        if (!text.trim()) return NodeFilter.FILTER_REJECT;

        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    nodes.forEach((node) => {
      const nextText = replacer(node.nodeValue || '');

      if (typeof nextText === 'string') {
        node.nodeValue = nextText;
      }
    });
  }
  // HOOYI_FAST_STICKY_CART_END
  /**
   * Updates product form immediately.
   *
   * 作用：
   * 1. 立刻更新 input[name="id"]，防止加购还是旧颜色旧尺码；
   * 2. 立刻启用/禁用 Add to cart；
   * 3. 立刻更新 Add to cart / Sold out 文案；
   * 4. 立刻同步加购动画用的小图。
   *
   * 还原方法：
   * 删除本方法，恢复原来的 updateHoooyiProductFormFast。
   *
   * @param {any} variant
   */
  updateHoooyiProductFormFast(variant) {
    if (!variant?.id) return;
    // HOOYI_FAST_QUICK_ADD_START
    // 作用：Quick add 弹窗里只更新弹窗内表单，不误改页面其他商品。
    // 还原：恢复为 const section = this.closest('.shopify-section') || document;
    const section = this.getHoooyiUpdateRoot();
    // HOOYI_FAST_QUICK_ADD_END
    const map = this.getHoooyiVariantMap?.();

    const buttonText =
      variant.available === false
        ? map?.button_texts?.sold_out || 'Sold out'
        : map?.button_texts?.add_to_cart || 'Add to cart';

    section.querySelectorAll('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = String(variant.id);
      }
    });

    section.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (variant.available) {
        container.enable?.();
      } else {
        container.disable?.();
      }

      const button = container.querySelector('[ref="addToCartButton"]');

      if (button instanceof HTMLButtonElement) {
        button.disabled = !variant.available;
        button.setAttribute('aria-disabled', String(!variant.available));

        // HOOYI_FAST_VARIANT_BUTTON_TEXT_START
        // 作用：
        // 本地切换 variant 后，立即同步按钮文案。
        // 不再等远程 section 返回后 morph button。
        //
        // 还原方法：
        // 删除 HOOYI_FAST_VARIANT_BUTTON_TEXT_START 到 HOOYI_FAST_VARIANT_BUTTON_TEXT_END 之间的代码。
        this.updateHoooyiAddToCartButtonText(button, buttonText, variant.available);
        // HOOYI_FAST_VARIANT_BUTTON_TEXT_END
      }

      if (variant.featured_media_preview_src) {
        container.setAttribute('data-product-variant-media', `${variant.featured_media_preview_src}&width=100`);
      }
    });

    section.querySelectorAll('.shopify-payment-button, shopify-accelerated-checkout, .accelerated-checkout-block').forEach((el) => {
      if (variant.available) {
        el.removeAttribute('hidden');
        el.classList.remove('hoooyi-payment-disabled');
        el.setAttribute('aria-disabled', 'false');
      } else {
        el.setAttribute('hidden', 'true');
        el.classList.add('hoooyi-payment-disabled');
        el.setAttribute('aria-disabled', 'true');
      }
    });
  }
  // HOOYI_FAST_PRODUCT_PRICE_START
  /**
   * Updates main product price immediately.
   *
   * 作用：
   * 切换 variant 后，立即更新主商品信息区价格，不等远程 section 返回。
   *
   * 还原：
   * 1. 删除本方法；
   * 2. 删除 hoooyiFastUpdateVariantData() 里 this.updateHoooyiProductPriceFast(preferredVariant) 调用；
   * 3. 删除 variant-main-picker.liquid 里的 price / compare_at_price 字段。
   *
   * @param {any} variant
   */
  updateHoooyiProductPriceFast(variant) {
    if (!variant?.price) return;

    // HOOYI_FAST_QUICK_ADD_START
    // 作用：Quick add 弹窗里只更新弹窗内价格。
    // 还原：恢复为 const section = this.closest('.shopify-section') || document;
    const section = this.getHoooyiUpdateRoot();
    // HOOYI_FAST_QUICK_ADD_END
    const priceText = this.cleanHoooyiMoneyText?.(variant.price) || variant.price || '';
    const compareAtPriceText = this.cleanHoooyiMoneyText?.(variant.compare_at_price) || variant.compare_at_price || '';
    const priceEls = section.querySelectorAll('product-price');

    priceEls.forEach((priceEl) => {
      if (!(priceEl instanceof HTMLElement)) return;

      // 避免误改底部 sticky bar 里的价格；这里只处理主商品区域里的 product-price。
      if (priceEl.closest('[style*="fixed"], .hoooyi-sticky, .sticky, floating-panel-component')) return;

      const regularPrice =
        priceEl.querySelector('.price-item--regular') ||
        priceEl.querySelector('.price-item') ||
        priceEl.querySelector('.price');

      if (regularPrice) {
        regularPrice.textContent = priceText;
      } else {
        priceEl.textContent = priceText;
      }


      const comparePrice = priceEl.querySelector('.compare-at-price, .price-item--compare, s');

      if (comparePrice) {
        if (compareAtPriceText) {
          comparePrice.textContent = compareAtPriceText;
          comparePrice.classList.remove('price__hidden');
          comparePrice.removeAttribute('hidden');
        } else {
          comparePrice.textContent = '';
          comparePrice.classList.add('price__hidden');
          comparePrice.setAttribute('hidden', 'true');
        }
      }
    });
  }
  // HOOYI_FAST_PRODUCT_PRICE_END
  // HOOYI_FAST_VARIANT_DATA_END

  // /**
  //  * Updates product form immediately.
  //  *
  //  * 作用：
  //  * 1. 立刻更新 input[name="id"]，防止加购还是旧颜色旧尺码。
  //  * 2. 立刻启用/禁用 Add to cart。
  //  * 3. 立刻同步 add-to-cart 动画用的小图。
  //  *
  //  * @param {any} variant
  //  */
  // updateHoooyiProductFormFast(variant) {
  //   if (!variant?.id) return;

  //   const section = this.closest('.shopify-section') || document;

  //   section.querySelectorAll('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]').forEach((input) => {
  //     if (input instanceof HTMLInputElement) {
  //       input.value = String(variant.id);
  //     }
  //   });

  //   section.querySelectorAll('add-to-cart-component').forEach((container) => {
  //     if (variant.available) {
  //       container.enable?.();
  //     } else {
  //       container.disable?.();
  //     }

  //     const button = container.querySelector('[ref="addToCartButton"]');

  //     if (button instanceof HTMLButtonElement) {
  //       button.disabled = !variant.available;
  //     }

  //     if (variant.featured_media_preview_src) {
  //       container.setAttribute('data-product-variant-media', `${variant.featured_media_preview_src}&width=100`);
  //     }
  //   });

  //   section.querySelectorAll('.shopify-payment-button').forEach((paymentButton) => {
  //     if (variant.available) {
  //       paymentButton.removeAttribute('hidden');
  //     } else {
  //       paymentButton.setAttribute('hidden', 'true');
  //     }
  //   });
  // }

  /**
   * Main local variant update.
   *
   * 这个方法是本地快速更新的入口。
   *
   * @param {HTMLElement} selectedOption
   * @returns {any | null}
   */
  hoooyiFastUpdateVariantData(selectedOption) {
    if (!(selectedOption instanceof HTMLElement)) return null;

    const map = this.getHoooyiVariantMap();

    if (!map?.variants?.length) return null;

    const currentProductUrl = this.dataset.productUrl?.split('?')[0];
    const connectedProductUrl = selectedOption.dataset.connectedProductUrl;

    // combined listing 切到另一个 product 时，当前页面 variant map 不包含新 product 的 variants。
    // 这种情况必须交给原主题远程请求处理，不能本地乱算。
    if (connectedProductUrl && connectedProductUrl !== currentProductUrl) {
      return null;
    }

    const changedIndex = Number.parseInt(selectedOption.dataset.fieldsetIndex || '0', 10);

    let selectedValues = this.getHoooyiSelectedOptionValues();

    const preferredVariant = this.findHoooyiPreferredVariant(map, selectedValues, changedIndex);

    if (!preferredVariant) return null;

    // 如果旧尺码在新颜色下不可用，自动切到该颜色下第一个可用尺码。
    this.syncHoooyiInputsToOptionValues(preferredVariant.options);

    selectedValues = this.getHoooyiSelectedOptionValues();

    this.updateHoooyiLegendTexts();
    this.updateHoooyiOptionAvailability(map);
    this.updateHoooyiProductFormFast(preferredVariant);

    // HOOYI_FAST_VARIANT_MEDIA_GALLERY_START
    // 作用：本地 variant 数据更新后，立即同步左侧缩略图 / slideshow 当前 slide。
    // 还原：删除这一行，并删除 updateHoooyiMediaGalleryFast 方法。
    this.updateHoooyiMediaGalleryFast(preferredVariant);
    // HOOYI_FAST_VARIANT_MEDIA_GALLERY_END

    // HOOYI_FAST_STICKY_CART_START
    // 作用：本地变体数据同步后，延迟同步底部 sticky 加购浮窗。
    // 原因：updateHoooyiStickyCartFast 会扫描页面 DOM，直接同步会阻塞主图/Color/Size 的即时绘制。
    // 还原：恢复为 this.updateHoooyiStickyCartFast(preferredVariant);
    if (!this.isHoooyiQuickAddContext?.()) {
      this.queueHoooyiStickyCartUpdate(preferredVariant);
    }
    // HOOYI_FAST_STICKY_CART_END

    // HOOYI_FAST_PRODUCT_PRICE_START
    // 作用：本地 variant 数据同步后，立即同步主商品价格。
    // 还原：删除这一行，并删除 updateHoooyiProductPriceFast 方法。
    this.updateHoooyiProductPriceFast(preferredVariant);
    // HOOYI_FAST_PRODUCT_PRICE_END

    // 让原主题后面的 URL 更新逻辑也拿到正确 variant id。
    selectedOption.dataset.variantId = String(preferredVariant.id);

    return {
      ...preferredVariant,
      featured_media: preferredVariant.featured_media_src
        ? {
            preview_image: {
              src: preferredVariant.featured_media_src,
            },
          }
        : undefined,
    };
  }
  // HOOYI_FAST_VARIANT_DATA_END

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // HOOYI_FETCH_RACE_GUARD_START
    // 作用：
    // 快速连续切色卡时，旧请求即使晚回来，也不能再 morph 覆盖新颜色状态。
    // 还原：
    // 删除 fetchToken 相关判断。
    this.__hoooyiFetchToken = (this.__hoooyiFetchToken || 0) + 1;
    const fetchToken = this.__hoooyiFetchToken;
    // HOOYI_FETCH_RACE_GUARD_END

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        // HOOYI_FETCH_RACE_GUARD_START
        // 作用：如果这不是最后一次切换发出的请求，直接丢弃旧响应。
        // 还原：删除这个 if。
        if (fetchToken !== this.__hoooyiFetchToken) return;
        // HOOYI_FETCH_RACE_GUARD_END

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        // HOOYI_FETCH_RACE_GUARD_START
        // 作用：进入 DOM morph 前再检查一次，防止旧响应覆盖新状态。
        // 还原：删除这个 if。
        if (fetchToken !== this.__hoooyiFetchToken) return;
        // HOOYI_FETCH_RACE_GUARD_END

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_START
  /**
   * Locks all unavailable option inputs.
   *
   * 作用：
   * 1. 把 data-option-available="false" / aria-disabled="true" 的 input 真正 disabled；
   * 2. 给 label 补上 hoooyi-option-unavailable class；
   * 3. 防止远程 section morph 后，不可用选项又变成可点击。
   *
   * 还原方法：
   * 删除本方法，并删除 updateVariantPicker() 里的 this.lockHoooyiUnavailableOptions()。
   */
  lockHoooyiUnavailableOptions() {
    const inputs = Array.from(this.querySelectorAll('fieldset input[type="radio"]'));

    inputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;

      const isUnavailable =
        input.dataset.optionAvailable === 'false' ||
        input.getAttribute('aria-disabled') === 'true' ||
        input.dataset.hoooyiOptionUnavailable === 'true';

      const label = input.closest('label');

      input.disabled = isUnavailable;

      if (isUnavailable) {
        input.setAttribute('aria-disabled', 'true');
        input.setAttribute('data-hoooyi-option-unavailable', 'true');
      } else {
        input.removeAttribute('aria-disabled');
        input.removeAttribute('data-hoooyi-option-unavailable');
      }

      if (label) {
        label.classList.toggle('hoooyi-option-unavailable', isUnavailable);
        label.toggleAttribute('data-hoooyi-option-unavailable', isUnavailable);
      }
    });
  }
  // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_END

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

      morph(this, newVariantPickerSource, {
        ...MORPH_OPTIONS,
        getNodeKey: (node) => {
          if (!(node instanceof HTMLElement)) return undefined;
          const key = node.dataset.key;
          return key;
        },
      });
      this.updateVariantPickerCss();

      // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_START
      // 作用：接口返回并 morph 替换 variant picker 后，重新把不可用选项 disabled。
      // 原因：远程返回的新 HTML 只有 aria-disabled / data-option-available=false，
      // 没有真正 disabled；如果不重新处理，过一会儿不可用选项又能点。
      // 还原：删除这一行，并删除 lockHoooyiUnavailableOptions 方法。
      this.lockHoooyiUnavailableOptions();
      // HOOYI_DISABLE_UNAVAILABLE_OPTIONS_END

      return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
